// Microsoft Clarity loader - session replay + heatmaps. Idempotent: calling
// load() twice is safe.
//
// CONSENT-GATED, and deliberately not in Umami's pre-consent tier. Clarity
// writes first-party cookies (_clck / _clsk) AND records a replay of what the
// visitor does on the page, so both triggers fire: ePrivacy device storage and
// GDPR personal-data processing. It belongs with GA4/GTM, behind consent.
//
// CSP: CloudFront must allow clarity.ms in script-src and *.clarity.ms +
// c.bing.com in connect-src (helinhair-infrastructure PR #63). Clarity splits
// tag delivery from data ingest, so with script-src alone the tag loads and
// then silently records nothing. It only fails behind CloudFront - localhost
// has no CSP - so this cannot be verified in dev.

import { isSensitiveRoute } from './redact'

const DEFAULT_CLARITY_ID = 'y2a6nbhlq8'
const SCRIPT_ATTR = 'data-helin-clarity'

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] }

interface ClarityWindow extends Window {
  clarity?: ClarityFn
}

export function getClarityId(): string {
  const fromEnv = (import.meta.env.VITE_CLARITY_ID as string | undefined)?.trim()
  return fromEnv && fromEnv.length > 0 ? fromEnv : DEFAULT_CLARITY_ID
}

export function isClarityLoaded(): boolean {
  return document.querySelector(`script[${SCRIPT_ATTR}]`) !== null
}

export function loadClarity(id: string = getClarityId()): void {
  if (isClarityLoaded()) return
  if (!id) return
  // Never start session replay on an order page: the replay would show the
  // buyer's name and address. syncClarityRoute loads it on the next page.
  if (isSensitiveRoute(window.location.pathname)) return

  // The vendor snippet installs a queueing stub so `clarity(...)` calls made
  // before the tag finishes downloading are replayed once it boots. Same
  // contract as GTM's dataLayer - keep it, or early calls are dropped.
  const w = window as ClarityWindow
  if (!w.clarity) {
    const stub: ClarityFn = (...args: unknown[]) => {
      stub.q = stub.q ?? []
      stub.q.push(args)
    }
    w.clarity = stub
  }

  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.clarity.ms/tag/${encodeURIComponent(id)}`
  script.setAttribute(SCRIPT_ATTR, id)
  document.head.appendChild(script)
}

let stopped = false

/**
 * SPA route change: stop Clarity's recording on the order pages and restart it
 * when the visitor leaves them. No-op while Clarity is not loaded.
 */
export function syncClarityRoute(pathname: string): void {
  const w = window as ClarityWindow
  if (!w.clarity || !isClarityLoaded()) return
  const sensitive = isSensitiveRoute(pathname)
  if (sensitive && !stopped) {
    w.clarity('stop')
    stopped = true
  } else if (!sensitive && stopped) {
    w.clarity('start')
    stopped = false
  }
}

export function _resetClarityForTesting(): void {
  stopped = false
}
